import React, { useState } from "react";

import { Lock } from "lucide-react";

import type {
  OrgAccess,
  OrgRoleAccess,
  RoleAccessLevel,
} from "../../Store/Reducers/AccountPlanAccessSlice";

/** What each level means, as the select and the confirm say it. */
export const LEVEL_LABEL: Record<RoleAccessLevel, string> = {
  published: "Published briefing only",
  view: "View full plan (read-only)",
  act: "Act as any role",
};

const LEVEL_HINT: Record<RoleAccessLevel, string> = {
  published:
    "Sees the briefing once it's published, like anyone in the organization.",
  view: "Sees the plan, its versions, slides and draft, with no buttons to change anything.",
  act: "Can upload, confirm, verify and publish, like the AE and the CSM.",
};

/** What a locked role does; set by the plan's section owners, not here. */
const LOCKED_LABEL: Record<NonNullable<OrgRoleAccess["locked"]>, string> = {
  author: "Authors the plan",
  verify: "Verifies its sections",
  sponsor: "Sponsor view (accounts they sponsor)",
};

const LEVELS: RoleAccessLevel[] = ["published", "view", "act"];

interface Props {
  org: OrgAccess;
  /** The role whose save is in flight, if any. */
  savingRole: string | null;
  onSave: (
    role: OrgRoleAccess,
    level: RoleAccessLevel,
    note: string
  ) => Promise<boolean>;
}

/**
 * "Who sees what" for one organization (Sushant, 2026-11-03): every org role in a
 * table, like Playbook Hub's org settings. AE, CSM and sponsor roles are locked (the
 * section-owner model); every other role picks one level, confirmed before saving.
 * Defaults: the org admin views the full plan, everyone else the published briefing.
 */
const RoleAccessPanel: React.FC<Props> = ({ org, savingRole, onSave }) => {
  const [pick, setPick] = useState<{
    role: OrgRoleAccess;
    level: RoleAccessLevel;
  } | null>(null);
  const [note, setNote] = useState("");
  const roles = org.roles || [];

  if (!roles.length)
    return (
      <p className="text-sm text-gray-500" data-testid="role-access-empty">
        This organization has no roles yet.
      </p>
    );

  return (
    <div data-testid={`role-access-${org.schema}`}>
      <p className="mb-2 text-xs text-gray-600">
        What each role in {org.name} gets in Account Plans. A change applies on
        the next page load.
      </p>
      <table className="w-full max-w-3xl text-left text-sm">
        <thead className="text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th scope="col" className="py-1.5 pr-4 font-semibold">
              Role
            </th>
            <th scope="col" className="py-1.5 pr-4 font-semibold">
              People
            </th>
            <th scope="col" className="py-1.5 font-semibold">
              Account Plans access
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {roles.map(r => {
            const selectId = `role-level-${org.schema}-${r.role}`;
            const shown =
              pick?.role.role === r.role ? pick.level : r.level || "published";
            return (
              <tr key={r.role} data-testid={`role-row-${r.role}`}>
                <td className="py-2 pr-4 font-medium text-gray-900">
                  {r.label}
                </td>
                <td className="py-2 pr-4 tabular-nums text-gray-600">
                  {r.users}
                </td>
                <td className="py-2">
                  {r.locked ? (
                    <span
                      className="inline-flex items-center gap-1.5 text-gray-600"
                      title="Set by the plan's section owners, not here"
                    >
                      <Lock className="h-3.5 w-3.5" aria-hidden />
                      {LOCKED_LABEL[r.locked]}
                    </span>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <label htmlFor={selectId} className="sr-only">
                        Account Plans access for {r.label}
                      </label>
                      <select
                        id={selectId}
                        value={shown}
                        disabled={savingRole !== null}
                        onChange={e => {
                          const level = e.target.value as RoleAccessLevel;
                          setNote("");
                          setPick(
                            level === r.level ? null : { role: r, level }
                          );
                        }}
                        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                      >
                        {LEVELS.map(l => (
                          <option key={l} value={l}>
                            {LEVEL_LABEL[l]}
                            {r.default === l ? " (default)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {pick && (
        <div
          role="group"
          aria-label={`Change ${pick.role.label} access`}
          data-testid="role-access-confirm"
          className="mt-3 max-w-3xl rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm"
        >
          <p className="font-medium text-gray-900">
            Set {pick.role.label} in {org.name} to {LEVEL_LABEL[pick.level]}?
          </p>
          <p className="mt-0.5 text-gray-600">
            {LEVEL_HINT[pick.level]} Affects {pick.role.users}{" "}
            {pick.role.users === 1 ? "person" : "people"}.
          </p>
          <label
            htmlFor={`role-note-${org.schema}`}
            className="mt-2 block text-xs text-gray-600"
          >
            Note for the change history (optional)
          </label>
          <input
            id={`role-note-${org.schema}`}
            value={note}
            onChange={e => setNote(e.target.value)}
            maxLength={500}
            className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={savingRole !== null}
              onClick={async () => {
                if (await onSave(pick.role, pick.level, note)) setPick(null);
              }}
              className="rounded-md bg-[#974ae4] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#8538d4] disabled:opacity-50"
            >
              Set {pick.role.label} to {LEVEL_LABEL[pick.level]}
            </button>
            <button
              type="button"
              onClick={() => setPick(null)}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleAccessPanel;
